import { useCallback, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Banner, ContentModal } from "@/shared/ui";
import { useAuth } from "@/shared/auth/AuthContext";
import { getOrganizationProfile, listFormInstances } from "@/modules/docs/api/docAdminApi";
import { useMesBatchQuery } from "@/modules/mes/queries/useMesQueries";
import { buildEbrPrintHtml } from "@/modules/print/lib/buildEbrPrintHtml";
import { printHtmlDocument } from "@/platform/printDocument";

export type EbrPrintPreviewTarget = {
  batchCode: string;
  title?: string;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: EbrPrintPreviewTarget | null;
};

export function EbrPrintPreviewDialog({ open, onOpenChange, target }: Props) {
  const { http } = useAuth();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const batchCode = target?.batchCode ?? "";
  const title =
    target?.title ?? (batchCode ? `Hồ sơ mẻ · ${batchCode}` : "In hồ sơ mẻ");

  const org = useQuery({
    queryKey: ["sys", "organization"],
    queryFn: () => getOrganizationProfile(http),
    enabled: open,
  });
  const batch = useMesBatchQuery(open && batchCode ? batchCode : null);
  const forms = useQuery({
    queryKey: ["doc", "instances", "batch", batch.data?.id, "print-preview"],
    queryFn: () =>
      listFormInstances(http, {
        page: 1,
        pageSize: 100,
        q: "",
        status: "all",
        filters: { batchId: batch.data!.id },
        batchId: batch.data!.id,
      }).then((p) => p.items),
    enabled: open && Boolean(batch.data?.id),
  });

  const html = useMemo(() => {
    if (!batch.data) return null;
    return buildEbrPrintHtml(batch.data, forms.data ?? [], org.data);
  }, [batch.data, forms.data, org.data]);

  const loading =
    open &&
    (batch.isLoading ||
      org.isLoading ||
      (Boolean(batch.data?.id) && forms.isLoading));
  const loadError =
    batch.isError
      ? batch.error instanceof Error
        ? batch.error.message
        : "Không tải được hồ sơ mẻ."
      : !loading && open && batchCode && !batch.data
        ? `Không tìm thấy mẻ ${batchCode}`
        : null;

  const canAct = Boolean(html) && !loading && !loadError;

  const printDocument = useCallback(() => {
    if (html) {
      void printHtmlDocument(html, title);
      return;
    }
    const win = iframeRef.current?.contentWindow;
    if (win) {
      win.focus();
      win.print();
    }
  }, [html, title]);

  return (
    <ContentModal
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={batchCode ? `Mẻ ${batchCode}` : undefined}
      lockOutsideClose
      primaryAction={{
        id: "print",
        label: "In",
        onClick: printDocument,
        disabled: !canAct,
      }}
    >
      <div className="space-y-3">
        {loading ? (
          <p className="m-0 text-body text-text-secondary">Đang tải bản in…</p>
        ) : null}
        {loadError ? <Banner tone="danger" title={loadError} /> : null}
        {html ? (
          <iframe
            ref={iframeRef}
            title={title}
            className="min-h-[min(70dvh,40rem)] w-full rounded-control border border-border bg-white"
            srcDoc={html}
            sandbox="allow-same-origin allow-modals"
          />
        ) : null}
      </div>
    </ContentModal>
  );
}
